import { FunctionComponent } from "react";
import { debounce } from "lodash";

import { WhitelabelSettingReadModel } from "../../../../../../../@api";

import { appSettingsKeys } from "../../../../../../models/app-setting-keys";
import { translationKeys } from "../../../../../../models/app-translation-keys";
import { applyCSSSetting } from "../../../../../../models/functions/apply-css-setting.function";
import { isValidFontFamily } from "../../../../../../models/functions/is-valid-font.function";
import { useWhitelabelService } from "../../../../../../services/ServiceHooks/whitelabelHook";
import { SettingList } from "../../Layout/SettingList/SettingList";
import { ICSSSettingListItem } from "../../Models/interfaces/css-setting-list-item.interface";
import { ISettingListItem } from "../../Models/interfaces/setting-list-item.interface";
import { SettingTextInput } from "../../SettingLibrary/TextInput/SettingTextInput";

import "./FontSettings.scss";

interface IFontSettingsProps {
  settings: WhitelabelSettingReadModel[];
  resetSetting: (key: string) => void;
}

export const FontSettings: FunctionComponent<IFontSettingsProps> = (props: IFontSettingsProps) => {
  const whitelabelService = useWhitelabelService();

  const fontSettingTranslationsKeys = translationKeys.adminArea.whiteLabel.areas.font.settings;

  const settingFontItemTemplate = (
    setting: ICSSSettingListItem,
    whitelabelSetting: WhitelabelSettingReadModel | undefined,
  ) => {
    const defaultFont = `"Source Sans Pro", Arial, sans-serif`;
    const font = whitelabelSetting?.customValue || whitelabelSetting?.defaultValue || defaultFont;

    return (
      <SettingTextInput
        value={font}
        onChange={(value: string) => onChange(setting, value)}
        validationFunction={setting.validationFunction}
        style={{ maxWidth: "100%", width: "24rem" }}
      />
    );
  };

  const settingList: ICSSSettingListItem[] = [
    {
      settingKey: appSettingsKeys["Font.Family"],
      labelKey: fontSettingTranslationsKeys.fontFamily.label,
      cssKeys: ["font-family"],
      cssPrefix: "customer-",
      hasReset: true,
      centerVertically: true,
      applyValueFunction: (setting: ICSSSettingListItem, value: any) =>
        applyCSSSetting(setting.cssPrefix ?? "", setting.cssKeys[0], value),
      itemTemplate: settingFontItemTemplate,
      updateFunction: whitelabelService.updateSetting.bind(whitelabelService),
      validationFunction: isValidFontFamily,
    },
  ];

  const onChange = (setting: ICSSSettingListItem, newFontValue: string) => {
    setting.applyValueFunction(setting, newFontValue);
    debounceBackendUpdateChange(setting, newFontValue);
  };

  const debounceBackendUpdateChange = debounce(
    (setting: ISettingListItem, value) => setting.updateFunction(setting.settingKey, value),
    300,
  );

  const resetSetting = async (setting: ICSSSettingListItem) => {
    setting.applyValueFunction(setting, "");
    props.resetSetting(setting.settingKey);
  };

  return <SettingList list={settingList} whiteLabelSettings={props.settings} resetSetting={resetSetting} />;
};
